"use client";
import React, { useRef, useState } from "react";

import FullScreen from "@/app/icons/fullScreenIcon";
import PauseIcon from "@/app/icons/pauseIcon";
import PlayIcon from "@/app/icons/playIcon";

const CONTROLS_VISIBILITY_DURATION_MS = 2000;

type VideoPlayerProps = {
  videoSrc: string;
  videoRef?: (el: HTMLVideoElement | null) => void;
  onPlay?: (video: HTMLVideoElement) => void;
  className?: string;
  onFullscreenClick?: () => void;
  type?: string;
  videoIconSize?: string;
  fullScreenSize?: string;
  fullScreenIconSize?: string;
};

const VideoPlayer = ({
  videoSrc,
  videoRef,
  onPlay,
  className = "",
  onFullscreenClick,
  type,
  videoIconSize,
  fullScreenSize,
  fullScreenIconSize,
}: VideoPlayerProps) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);

  const handleTap = () => {
    if (isPlaying) {
      setShowControls(true);
      setTimeout(() => setShowControls(false), CONTROLS_VISIBILITY_DURATION_MS);
    }
  };

  const handlePlay = () => {
    if (localVideoRef.current) {
      localVideoRef.current.play();
      setIsPlaying(true);
      onPlay?.(localVideoRef.current);
    }
  };

  const handlePause = () => {
    localVideoRef.current?.pause();
    setIsPlaying(false);
  };

  const handleEnded = () => {
    if (localVideoRef.current) localVideoRef.current.currentTime = 0;
    setIsPlaying(false);
  };

  const onFullScreen = () => {
    handlePause();
    onFullscreenClick?.();
  };

  return (
    <div className={`relative group  ${className}`} onClick={handleTap}>
      <video
        ref={(el) => {
          localVideoRef.current = el;
          videoRef?.(el);
        }}
        className={`${
          type === "singleVideo" ? "rounded-lg" : "rounded-t-lg"
        } w-full h-full object-cover`}
        onPlay={(e) => {
          setIsPlaying(true);
          onPlay?.(e.currentTarget);
        }}
        onPause={() => setIsPlaying(false)}
        onEnded={handleEnded}
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
      <div className="absolute z-20 flex items-center justify-center inset-0 bg-regal-cover rounded-lg">
        {!isPlaying ? (
          <div
            onClick={handlePlay}
            className={`flex justify-center items-center w-30 h-30 cursor-pointer z-30 ${videoIconSize}`}
          >
            <PlayIcon className="text-white" />
          </div>
        ) : (
          <div
            onClick={handlePause}
            className={`
            flex justify-center items-center cursor-pointer
            transition-opacity duration-300
            opacity-0 group-hover:opacity-100 w-30 h-30 z-30 ${videoIconSize}
            ${showControls ? "opacity-100" : ""}
          `}
          >
            <PauseIcon className="text-white" />
          </div>
        )}
      </div>
      {onFullscreenClick && (
        <div
          className={`flex absolute cursor-pointer w-8 h-8 items-center justify-center rounded-md bg-regal-white-blur z-50 bottom-2 right-2 ${fullScreenSize}`}
          onClick={onFullScreen}
        >
          <div className={`w-6 h-6 ${fullScreenIconSize}`}>
            <FullScreen className="text-white" />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoPlayer;
